(() => {
	'use strict';

	const TPT_DISCOUNT_COUNTDOWN_TAG = 'tpt-discount-countdown';

	if (customElements.get(TPT_DISCOUNT_COUNTDOWN_TAG)) {
		return;
	}

	class TptDiscountCountdown extends HTMLElement {
		constructor() {
			super();

			this.tptInterval = null;
			this.tptCopyTimeout = null;

			this.tptHandleCopy = this.tptHandleCopy.bind(this);
			this.tptHandleVisibilityChange = this.tptHandleVisibilityChange.bind(this);
		}

		connectedCallback() {
			if (this.tptInitialized) {
				return;
			}

			this.tptInitialized = true;

			this.tptCacheElements();
			this.tptBindEvents();
			this.tptInit();
		}

		disconnectedCallback() {
			this.tptDestroy();
		}

		/**
		 * Cache DOM elements
		 */
		tptCacheElements() {
			this.tptTimer = this.querySelector('[data-tpt-countdown-timer]');

			this.tptDays = this.querySelector('[data-tpt-countdown-days]');

			this.tptHours = this.querySelector('[data-tpt-countdown-hours]');

			this.tptMinutes = this.querySelector('[data-tpt-countdown-minutes]');

			this.tptSeconds = this.querySelector('[data-tpt-countdown-seconds]');

			this.tptCopyButton = this.querySelector('[data-tpt-copy-discount]');

			this.tptDiscountCode = this.querySelector('[data-tpt-discount-code]');

			this.tptCopyLabel = this.querySelector('[data-tpt-copy-label]');

			this.tptCopyMessage = this.querySelector('[data-tpt-copy-message]');
		}

		/**
		 * Bind events
		 */
		tptBindEvents() {
			if (this.tptCopyButton) {
				this.tptCopyButton.addEventListener('click', this.tptHandleCopy);
			}

			document.addEventListener('visibilitychange', this.tptHandleVisibilityChange);
		}

		/**
		 * Main initialization
		 */
		tptInit() {
			const shouldShow = this.tptShouldShowBlock();

			if (!shouldShow) {
				this.tptHideBlock();
				return;
			}

			this.tptShowBlock();

			if (this.tptCountdownEnabled()) {
				this.tptStartCountdown();
			}
		}

		/**
		 * Check whether countdown is enabled
		 */
		tptCountdownEnabled() {
			return this.dataset.countdownEnabled === 'true';
		}

		/**
		 * Get repeat days
		 */
		tptGetRepeatDays() {
			const value = parseInt(this.dataset.countdownRepeatDays || '0', 10);

			if (Number.isNaN(value)) {
				return 0;
			}

			return Math.max(0, value);
		}

		/**
		 * Get storage key
		 */
		tptGetStorageKey() {
			return this.dataset.countdownStorageKey || 'tptCoundown24h';
		}

		/**
		 * Get current local date
		 *
		 * Format:
		 * YYYY-MM-DD
		 */
		tptGetTodayKey() {
			const now = new Date();

			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, '0');

			const day = String(now.getDate()).padStart(2, '0');

			return `${year}-${month}-${day}`;
		}

		/**
		 * Convert YYYY-MM-DD to local Date
		 */
		tptParseDate(dateString) {
			if (!dateString) {
				return null;
			}

			const parts = dateString.split('-');

			if (parts.length !== 3) {
				return null;
			}

			const year = parseInt(parts[0], 10);
			const month = parseInt(parts[1], 10);
			const day = parseInt(parts[2], 10);

			if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
				return null;
			}

			const date = new Date(year, month - 1, day);

			if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
				return null;
			}

			return date;
		}

		/**
		 * Calculate calendar days between two local dates
		 */
		tptGetCalendarDaysDifference(fromDateString, toDateString) {
			const fromDate = this.tptParseDate(fromDateString);

			const toDate = this.tptParseDate(toDateString);

			if (!fromDate || !toDate) {
				return null;
			}

			fromDate.setHours(0, 0, 0, 0);
			toDate.setHours(0, 0, 0, 0);

			const difference = toDate.getTime() - fromDate.getTime();

			return Math.floor(difference / 86400000);
		}

		/**
		 * Determine whether block should be shown
		 *
		 * repeat_after_days:
		 *
		 * 0 = show once ever
		 *
		 * 1 = show again next calendar day
		 *
		 * 2 = show again after 2 calendar days
		 *
		 * 7 = show again after 7 calendar days
		 */
		tptShouldShowBlock() {
			let lastShownDate = null;

			try {
				lastShownDate = localStorage.getItem(this.tptGetStorageKey());
			} catch (error) {
				return true;
			}

			const today = this.tptGetTodayKey();

			/**
			 * First visit
			 */
			if (!lastShownDate) {
				this.tptSaveShownDate(today);
				return true;
			}

			/**
			 * Already shown today
			 */
			if (lastShownDate === today) {
				return true;
			}

			/**
			 * Show only once
			 */
			const repeatDays = this.tptGetRepeatDays();

			if (repeatDays <= 0) {
				return false;
			}

			/**
			 * Calculate calendar days
			 */
			const elapsedDays = this.tptGetCalendarDaysDifference(lastShownDate, today);

			/**
			 * Invalid stored date
			 */
			if (elapsedDays === null || elapsedDays < 0) {
				this.tptSaveShownDate(today);
				return true;
			}

			/**
			 * Not enough days have passed
			 */
			if (elapsedDays < repeatDays) {
				return false;
			}

			/**
			 * Eligible again
			 */
			this.tptSaveShownDate(today);

			return true;
		}

		/**
		 * Save last shown date
		 */
		tptSaveShownDate(dateString) {
			try {
				localStorage.setItem(this.tptGetStorageKey(), dateString);
			} catch (error) {
				// localStorage can be unavailable
			}
		}

		/**
		 * Show whole block
		 */
		tptShowBlock() {
			this.hidden = false;
		}

		/**
		 * Hide whole block
		 */
		tptHideBlock() {
			this.hidden = true;

			this.tptStopCountdown();
		}

		/**
		 * Start countdown
		 */
		tptStartCountdown() {
			if (!this.tptTimer) {
				return;
			}

			if (!this.tptDays || !this.tptHours || !this.tptMinutes || !this.tptSeconds) {
				return;
			}

			this.tptStopCountdown();

			this.tptUpdateCountdown();

			this.tptInterval = setInterval(() => {
				this.tptUpdateCountdown();
			}, 1000);
		}

		/**
		 * Stop countdown
		 */
		tptStopCountdown() {
			if (this.tptInterval) {
				clearInterval(this.tptInterval);
				this.tptInterval = null;
			}
		}

		/**
		 * Get local midnight
		 */
		tptGetEndOfDay() {
			const endOfDay = new Date();

			endOfDay.setHours(24, 0, 0, 0);

			return endOfDay;
		}

		/**
		 * Update countdown
		 */
		tptUpdateCountdown() {
			const now = new Date();
			const endOfDay = this.tptGetEndOfDay();

			const remaining = endOfDay.getTime() - now.getTime();

			/**
			 * Day changed
			 */
			if (remaining <= 0) {
				this.tptHandleDayEnded();
				return;
			}

			const totalSeconds = Math.floor(remaining / 1000);

			const days = Math.floor(totalSeconds / 86400);

			const hours = Math.floor((totalSeconds % 86400) / 3600);

			const minutes = Math.floor((totalSeconds % 3600) / 60);

			const seconds = totalSeconds % 60;

			this.tptDays.textContent = this.tptPadNumber(days);

			this.tptHours.textContent = this.tptPadNumber(hours);

			this.tptMinutes.textContent = this.tptPadNumber(minutes);

			this.tptSeconds.textContent = this.tptPadNumber(seconds);
		}

		/**
		 * Pad number
		 */
		tptPadNumber(number) {
			return String(number).padStart(2, '0');
		}

		/**
		 * Handle midnight
		 */
		tptHandleDayEnded() {
			this.tptStopCountdown();

			/**
			 * Entire block disappears
			 */
			this.tptHideBlock();
		}

		/**
		 * Handle browser tab visibility
		 *
		 * Useful when user leaves the tab
		 * open overnight.
		 */
		tptHandleVisibilityChange() {
			if (document.hidden) {
				return;
			}

			if (this.hidden) {
				return;
			}

			if (!this.tptCountdownEnabled()) {
				return;
			}

			this.tptUpdateCountdown();
		}

		/**
		 * Copy discount code
		 */
		async tptHandleCopy() {
			if (!this.tptDiscountCode) {
				return;
			}

			const code = this.tptDiscountCode.textContent.trim();

			if (!code) {
				return;
			}

			const copied = await this.tptCopyText(code);

			if (!copied) {
				return;
			}

			this.tptSetCopiedState();
		}

		/**
		 * Copy text
		 */
		async tptCopyText(text) {
			/**
			 * Modern Clipboard API
			 */
			if (navigator.clipboard && window.isSecureContext) {
				try {
					await navigator.clipboard.writeText(text);

					return true;
				} catch (error) {
					// Continue to fallback
				}
			}

			/**
			 * Fallback
			 */
			try {
				const textarea = document.createElement('textarea');

				textarea.value = text;

				textarea.setAttribute('readonly', '');

				textarea.style.position = 'fixed';

				textarea.style.opacity = '0';
				textarea.style.pointerEvents = 'none';

				document.body.appendChild(textarea);

				textarea.select();
				textarea.setSelectionRange(0, textarea.value.length);

				const successful = document.execCommand('copy');

				textarea.remove();

				return successful;
			} catch (error) {
				return false;
			}
		}

		/**
		 * Set copied UI state
		 */
		tptSetCopiedState() {
			const copyLabel = this.dataset.copyLabel || 'COPY';

			const copiedLabel = this.dataset.copiedLabel || 'COPIED';

			const copiedMessage = this.dataset.copiedMessage || 'Code copied!';

			if (this.tptCopyLabel) {
				this.tptCopyLabel.textContent = copiedLabel;
			}

			if (this.tptCopyButton) {
				this.tptCopyButton.classList.add('is-copied');

				this.tptCopyButton.setAttribute('aria-label', copiedLabel);
			}

			if (this.tptCopyMessage) {
				this.tptCopyMessage.textContent = copiedMessage;

				this.tptCopyMessage.classList.add('is-visible');
			}

			clearTimeout(this.tptCopyTimeout);

			this.tptCopyTimeout = setTimeout(() => {
				if (this.tptCopyLabel) {
					this.tptCopyLabel.textContent = copyLabel;
				}

				if (this.tptCopyButton) {
					this.tptCopyButton.classList.remove('is-copied');

					this.tptCopyButton.setAttribute('aria-label', copyLabel);
				}

				if (this.tptCopyMessage) {
					this.tptCopyMessage.classList.remove('is-visible');
				}
			}, 2500);
		}

		/**
		 * Destroy instance
		 */
		tptDestroy() {
			this.tptStopCountdown();

			clearTimeout(this.tptCopyTimeout);

			if (this.tptCopyButton) {
				this.tptCopyButton.removeEventListener('click', this.tptHandleCopy);
			}

			document.removeEventListener('visibilitychange', this.tptHandleVisibilityChange);

			this.tptInitialized = false;
		}
	}

	customElements.define(TPT_DISCOUNT_COUNTDOWN_TAG, TptDiscountCountdown);
})();
